import { SNIPPET_CATEGORY_KEYS, snippetCategoryLabel } from '@app/constants/snippetCategoryKeys';
import {
  ServiceOption,
  useAvailableServicesForCategoryQuery,
} from '@dieterApi/snippet/useAvailableServicesForCategoryQuery';
import { ServiceTicket, useServiceTicketResolveMutation } from '@dieterApi/serviceTicket';
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

interface Props {
  ticket: ServiceTicket | null;
  onClose: () => void;
  onResolved: () => void;
}

const normalize = (name: string | null | undefined) => (name ?? '').trim().toLowerCase();

// Schließen eines NEW_SERVICE-Tickets mit Rückmeldung an den Kunden: optional
// ein Snippet, dessen Dienst in die Auswahl des Kunden übernommen wird, und
// ein Freitext; beides geht per Mail an die Kontakt-E-Mail des Tickets.
export function ServiceTicketResolveDialog({ ticket, onClose, onResolved }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [category, setCategory] = useState<string>('');
  const [service, setService] = useState<ServiceOption | null>(null);
  const [message, setMessage] = useState('');
  const [notify, setNotify] = useState(true);
  const [resolve, { loading: resolving }] = useServiceTicketResolveMutation();

  const { data: servicesData, loading: servicesLoading } = useAvailableServicesForCategoryQuery(category || null);
  const services = servicesData?.availableServicesForCategory ?? [];
  const canNotify = !!ticket?.contactEmail && !ticket?.customerNotifiedAt;

  useEffect(() => {
    if (!ticket) return;
    setCategory(ticket.category ?? '');
    setService(null);
    setMessage('');
    setNotify(!!ticket.contactEmail && !ticket.customerNotifiedAt);
  }, [ticket]);

  // Gleichnamigen Dienst aus dem Katalog vorschlagen, sobald die Liste da ist
  useEffect(() => {
    if (!ticket || service || !services.length) return;
    const match = services.find((option) => normalize(option.serviceName) === normalize(ticket.serviceName));
    if (match) setService(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, ticket]);

  const missingContent = notify && !service && !message.trim();

  const handleSubmit = async () => {
    if (!ticket) return;
    try {
      await resolve({
        variables: {
          id: ticket.id,
          snippetId: service?.id ?? null,
          message: message.trim() || null,
          notify: notify && canNotify,
        },
      });
      enqueueSnackbar(notify && canNotify ? 'Ticket geschlossen, Kunde benachrichtigt' : 'Ticket geschlossen', {
        variant: 'success',
      });
      onResolved();
    } catch (error: any) {
      enqueueSnackbar(`Fehler beim Schließen: ${error.message}`, { variant: 'error' });
    }
  };

  return (
    <Dialog open={!!ticket} onClose={resolving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Ticket schließen: {ticket?.serviceName}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <div style={{ fontSize: '0.875rem', color: '#666' }}>
          {ticket?.companyName} · gemeldet unter {ticket?.serviceKey}
        </div>
        <FormControl size="small" fullWidth>
          <InputLabel id="resolve-category">Kategorie</InputLabel>
          <Select
            labelId="resolve-category"
            label="Kategorie"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value);
              setService(null);
            }}
          >
            <MenuItem value="">— kein Snippet übernehmen —</MenuItem>
            {SNIPPET_CATEGORY_KEYS.map((key) => (
              <MenuItem key={key} value={key}>
                {snippetCategoryLabel(key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {category && (
          <Autocomplete
            size="small"
            options={services}
            loading={servicesLoading}
            value={service}
            onChange={(_, value) => setService(value)}
            getOptionLabel={(option) => option.serviceName}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Snippet in die Auswahl des Kunden übernehmen"
                helperText="Leer lassen, wenn der Dienst nicht aufgenommen wird"
              />
            )}
          />
        )}
        <TextField
          label="Antwort an Kunden"
          multiline
          minRows={4}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={
            service
              ? 'Optional – die Mail nennt den übernommenen Dienst bereits'
              : 'z. B. warum der Dienst nicht aufgenommen wurde'
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={notify && canNotify}
              disabled={!canNotify}
              onChange={(e) => setNotify(e.target.checked)}
            />
          }
          label={
            ticket?.customerNotifiedAt
              ? `Kunde wurde bereits am ${new Date(ticket.customerNotifiedAt).toLocaleDateString('de-DE')} benachrichtigt`
              : ticket?.contactEmail
                ? `Kunde per Mail benachrichtigen (${ticket.contactEmail})`
                : 'Keine Kontakt-E-Mail am Ticket'
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={resolving}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={resolving || (canNotify && missingContent)}
          startIcon={resolving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {notify && canNotify ? 'Schließen & senden' : 'Schließen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
